import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import apiService from '../services/apiService';
import Modal from '../components/Modal';
import {
    MQTT_ENV_OPTIONS,
    readStoredMqttEnv,
    writeStoredMqttEnv,
} from '../utils/mqttEnv';

const ALL_INDIA_KEY = '__ALL__';

function emptyStateTable() {
    return {
        stations: [],
        total: 0,
        page: 1,
        pageSize: 100,
        loading: false,
        fetched: false,
        selectAll: false,
        includeIds: [],
        excludeIds: [],
        search: '',
    };
}

const FirmwareList = () => {
    const { isAdmin } = useAuth();
    const [firmware, setFirmware] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        device_id: '',
        version: '',
        limit: 50
    });
    const [publishModal, setPublishModal] = useState({ open: false, firmwareId: null, deviceName: '', version: '' });
    const [publishTab, setPublishTab] = useState('quick');
    const [mqttEnv, setMqttEnv] = useState(() => readStoredMqttEnv());

    // Quick OTA
    const [publishDeviceIdChips, setPublishDeviceIdChips] = useState([]);
    const [publishDeviceIdInput, setPublishDeviceIdInput] = useState('');
    const [publishStationIdChips, setPublishStationIdChips] = useState([]);
    const [publishStationIdInput, setPublishStationIdInput] = useState('');
    const [publishSchedule, setPublishSchedule] = useState('immediate');
    const [publishScheduleAt, setPublishScheduleAt] = useState('');
    const [publishPreview, setPublishPreview] = useState(null);
    const [publishLoading, setPublishLoading] = useState(false);
    const [publishResult, setPublishResult] = useState(null);

    // Advanced OTA — state multi-select + per-state paginated tables
    const [indiaStates, setIndiaStates] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]); // empty = All India
    const [statesPickerOpen, setStatesPickerOpen] = useState(false);
    const [stateTables, setStateTables] = useState({}); // key -> table state
    const [advancedInitialized, setAdvancedInitialized] = useState(false);
    const [skuFilter, setSkuFilter] = useState('');
    const [chipTarget, setChipTarget] = useState('esp32');
    const [advancedPreview, setAdvancedPreview] = useState(null);
    const [topicsExpanded, setTopicsExpanded] = useState(false);
    const [devicesSectionOpen, setDevicesSectionOpen] = useState(false);
    const [expandedStationCards, setExpandedStationCards] = useState({});
    const [otaJobProgress, setOtaJobProgress] = useState(null);

    const PREVIEW_STATIONS_UI_LIMIT = 5;
    const PREVIEW_DEVICES_PER_STATION_UI = 8;

    const tableKeys = useMemo(
        () => (selectedStates.length === 0 ? [ALL_INDIA_KEY] : selectedStates),
        [selectedStates]
    );

    const buildStateSelections = useCallback(() => {
        return tableKeys
            .map((key) => {
                const t = stateTables[key] || emptyStateTable();
                return {
                    state: key === ALL_INDIA_KEY ? null : key,
                    select_all: !!t.selectAll,
                    include_ids: t.includeIds || [],
                    exclude_ids: t.excludeIds || [],
                };
            })
            .filter((sel) => sel.select_all || (sel.include_ids && sel.include_ids.length > 0));
    }, [tableKeys, stateTables]);

    const hasStationSelection = buildStateSelections().length > 0;

    const setMqttEnvAndPersist = useCallback((next) => {
        setMqttEnv(writeStoredMqttEnv(next));
    }, []);

    const fetchPublishPreview = useCallback(async (firmwareId, deviceId, stationId, env) => {
        if (!firmwareId) return;
        try {
            const res = await apiService.getPublishPreview(firmwareId, {
                deviceId,
                stationId,
                env: env || mqttEnv,
            });
            if (res.success && res.data) setPublishPreview(res.data);
        } catch (e) {
            setPublishPreview(null);
        }
    }, [mqttEnv]);

    const effectiveSkuFilter = skuFilter || publishModal.deviceName || '';

    const skuOptions = useMemo(() => {
        const opts = [{ value: '', label: 'Firmware SKU (default)' }];
        for (const d of devices || []) {
            const ota = d.platformio_env ? ` → ${d.platformio_env}` : '';
            opts.push({
                value: d.name,
                label: `${d.name}${d.sku_name ? ` (${d.sku_name})` : ''}${ota}`,
            });
        }
        return opts;
    }, [devices]);

    const fetchAdvancedPreview = useCallback(async (firmwareId, selections, sku, env) => {
        if (!firmwareId || !selections.length) {
            setAdvancedPreview(null);
            return;
        }
        try {
            const res = await apiService.getAdvancedPublishPreview(firmwareId, {
                state_selections: selections,
                env: env || mqttEnv,
                // Always send designated SKU so device list is filtered
                sku_filter: sku || undefined,
            });
            if (res.success && res.data) setAdvancedPreview(res.data);
        } catch (e) {
            setAdvancedPreview(null);
        }
    }, [mqttEnv]);

    const loadStateTablePage = useCallback(async (key, page = 1, search = '') => {
        setStateTables((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || emptyStateTable()), loading: true, page, search },
        }));
        try {
            const opts = {
                page,
                search: search || undefined,
            };
            if (key !== ALL_INDIA_KEY) opts.state = key;
            const res = await apiService.getStations(opts);
            setStateTables((prev) => {
                const cur = prev[key] || emptyStateTable();
                return {
                    ...prev,
                    [key]: {
                        ...cur,
                        stations: res.data || [],
                        total: res.total || 0,
                        page: res.page || page,
                        pageSize: res.pageSize || cur.pageSize || 100,
                        loading: false,
                        fetched: true,
                        search,
                    },
                };
            });
        } catch (e) {
            console.error('Load stations page error:', e);
            setStateTables((prev) => ({
                ...prev,
                [key]: {
                    ...(prev[key] || emptyStateTable()),
                    stations: [],
                    total: 0,
                    loading: false,
                    fetched: true,
                },
            }));
        }
    }, []);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadFirmware();
    }, [filters]);

    // Preview only uses completed chips — not in-progress input text
    const firstDeviceIdForPreview = publishDeviceIdChips[0] || '';
    const firstStationIdForPreview = publishStationIdChips[0] || '';

    useEffect(() => {
        if (!publishModal.open || !publishModal.firmwareId || publishTab !== 'quick') return;
        const t = setTimeout(
            () =>
                fetchPublishPreview(
                    publishModal.firmwareId,
                    firstDeviceIdForPreview,
                    firstStationIdForPreview,
                    mqttEnv
                ),
            200
        );
        return () => clearTimeout(t);
    }, [
        publishModal.open,
        publishModal.firmwareId,
        firstDeviceIdForPreview,
        firstStationIdForPreview,
        publishTab,
        mqttEnv,
        fetchPublishPreview,
    ]);

    // Defer Advanced data load until Advanced tab is opened; refetch when states change
    useEffect(() => {
        if (!publishModal.open || publishTab !== 'advanced') return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.getIndiaStates();
                if (!cancelled && res.success) setIndiaStates(res.data || []);
            } catch (e) {
                console.error('Load India states error:', e);
            }
            if (cancelled) return;
            setAdvancedInitialized(true);
            const keys = selectedStates.length === 0 ? [ALL_INDIA_KEY] : selectedStates;
            // Snapshot which keys still need an initial fetch
            const keysToFetch = await new Promise((resolve) => {
                setStateTables((prev) => {
                    const need = [];
                    const next = { ...prev };
                    for (const key of keys) {
                        if (!next[key]) next[key] = emptyStateTable();
                        if (!next[key].fetched && !next[key].loading) {
                            need.push(key);
                            next[key] = { ...next[key], loading: true };
                        }
                    }
                    resolve(need);
                    return next;
                });
            });
            for (const key of keysToFetch) {
                if (cancelled) return;
                await loadStateTablePage(key, 1, '');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [publishModal.open, publishTab, selectedStates, loadStateTablePage]);

    useEffect(() => {
        if (!publishModal.open || !publishModal.firmwareId || publishTab !== 'advanced') return;
        const selections = buildStateSelections();
        const t = setTimeout(
            () =>
                fetchAdvancedPreview(
                    publishModal.firmwareId,
                    selections,
                    effectiveSkuFilter || undefined,
                    mqttEnv
                ),
            400
        );
        return () => clearTimeout(t);
    }, [
        publishModal.open,
        publishModal.firmwareId,
        effectiveSkuFilter,
        publishTab,
        mqttEnv,
        stateTables,
        selectedStates,
        fetchAdvancedPreview,
        buildStateSelections,
    ]);

    const loadData = async () => {
        try {
            const devicesResponse = await apiService.getDevices();
            if (devicesResponse.success) {
                setDevices(devicesResponse.data);
            }
        } catch (error) {
            console.error('Load devices error:', error);
        }
    };

    const loadFirmware = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await apiService.getFirmware(filters);
            if (response.success) {
                setFirmware(response.data);
            } else {
                setError(response.error || 'Failed to load firmware');
            }
        } catch (error) {
            console.error('Load firmware error:', error);
            setError('Failed to load firmware');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (firmwareId, fileName) => {
        try {
            const result = await apiService.downloadFirmware(firmwareId);
            apiService.downloadBlob(result.blob, result.filename);
        } catch (error) {
            console.error('Download error:', error);
            alert('Download failed: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDelete = async (firmwareId, deviceName, version) => {
        if (!window.confirm(`Are you sure you want to delete ${deviceName} v${version}?`)) {
            return;
        }

        try {
            const response = await apiService.deleteFirmware(firmwareId);
            if (response.success) {
                loadFirmware();
            } else {
                alert('Delete failed: ' + response.error);
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('Delete failed: ' + (error.response?.data?.error || error.message));
        }
    };

    const openPublishModal = (firmwareId, deviceName, version) => {
        setPublishModal({ open: true, firmwareId, deviceName, version });
        setPublishTab('quick');
        setPublishDeviceIdChips([]);
        setPublishDeviceIdInput('');
        setPublishStationIdChips([]);
        setPublishStationIdInput('');
        setPublishSchedule('immediate');
        setPublishScheduleAt('');
        setPublishPreview(null);
        setPublishResult(null);
        setSkuFilter('');
        setChipTarget('esp32');
        setAdvancedPreview(null);
        setTopicsExpanded(false);
        setDevicesSectionOpen(false);
        setExpandedStationCards({});
        setSelectedStates([]);
        setStatesPickerOpen(false);
        setStateTables({});
        setAdvancedInitialized(false);
        setOtaJobProgress(null);
        if (firmwareId) fetchPublishPreview(firmwareId, '', '');
        // Stations load deferred until Advanced tab opens
    };

    const closePublishModal = () => {
        setPublishModal({ open: false, firmwareId: null, deviceName: '', version: '' });
        setPublishResult(null);
        setOtaJobProgress(null);
    };

    const addDeviceIdChipsFromInput = () => {
        const parts = publishDeviceIdInput.trim().split(/[\s,]+/).filter(Boolean);
        if (parts.length === 0) return;
        setPublishDeviceIdChips((prev) => [...prev, ...parts]);
        setPublishDeviceIdInput('');
    };

    const removeDeviceIdChip = (index) => {
        setPublishDeviceIdChips((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePublishDeviceIdKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            addDeviceIdChipsFromInput();
        }
    };

    const handlePublishDeviceIdPaste = (e) => {
        const pasted = e.clipboardData.getData('text');
        const parts = pasted.split(/[\s,]+/).filter(Boolean);
        if (parts.length > 0) {
            e.preventDefault();
            setPublishDeviceIdChips((prev) => [...prev, ...parts]);
        }
    };

    const addStationIdChipsFromInput = () => {
        const parts = publishStationIdInput.trim().split(/[\s,]+/).filter(Boolean);
        if (parts.length === 0) return;
        // Quick OTA uses one station in the MQTT path — keep a single chip
        setPublishStationIdChips([parts[parts.length - 1]]);
        setPublishStationIdInput('');
    };

    const removeStationIdChip = (index) => {
        setPublishStationIdChips((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePublishStationIdKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            addStationIdChipsFromInput();
        }
    };

    const handlePublishStationIdPaste = (e) => {
        const pasted = e.clipboardData.getData('text');
        const parts = pasted.split(/[\s,]+/).filter(Boolean);
        if (parts.length > 0) {
            e.preventDefault();
            setPublishStationIdChips([parts[parts.length - 1]]);
            setPublishStationIdInput('');
        }
    };

    const toggleStateChip = (stateName) => {
        setSelectedStates((prev) => {
            if (prev.includes(stateName)) return prev.filter((s) => s !== stateName);
            return [...prev, stateName].sort();
        });
    };

    const removeStateChip = (stateName) => {
        setSelectedStates((prev) => prev.filter((s) => s !== stateName));
    };

    const isStationChecked = (key, stationId) => {
        const t = stateTables[key];
        if (!t) return false;
        if (t.selectAll) return !(t.excludeIds || []).includes(stationId);
        return (t.includeIds || []).includes(stationId);
    };

    const toggleSelectAllStations = (key) => {
        setStateTables((prev) => {
            const cur = prev[key] || emptyStateTable();
            const next = !cur.selectAll;
            return {
                ...prev,
                [key]: {
                    ...cur,
                    selectAll: next,
                    includeIds: [],
                    excludeIds: [],
                },
            };
        });
    };

    const toggleStationInTable = (key, stationId) => {
        setStateTables((prev) => {
            const cur = prev[key] || emptyStateTable();
            if (cur.selectAll) {
                const exclude = new Set(cur.excludeIds || []);
                if (exclude.has(stationId)) exclude.delete(stationId);
                else exclude.add(stationId);
                return { ...prev, [key]: { ...cur, excludeIds: [...exclude] } };
            }
            const include = new Set(cur.includeIds || []);
            if (include.has(stationId)) include.delete(stationId);
            else include.add(stationId);
            return { ...prev, [key]: { ...cur, includeIds: [...include] } };
        });
    };

    const pollOtaJob = async (jobId) => {
        const maxAttempts = 120;
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const res = await apiService.getOtaJob(jobId);
                if (res.success && res.data) {
                    setOtaJobProgress(res.data);
                    if (res.data.status === 'completed' || res.data.status === 'failed') {
                        return res.data;
                    }
                }
            } catch (e) {
                console.warn('OTA job poll error:', e);
            }
            await new Promise((r) => setTimeout(r, 1500));
        }
        return null;
    };

    const handlePublishSubmit = async () => {
        if (!publishModal.firmwareId) return;
        setPublishLoading(true);
        setPublishResult(null);
        setOtaJobProgress(null);
        try {
            if (publishTab === 'advanced') {
                const selections = buildStateSelections();
                if (selections.length === 0) {
                    setPublishResult({
                        type: 'error',
                        message: 'Select at least one station, or check All stations for a state',
                    });
                    setPublishLoading(false);
                    return;
                }
                const body = {
                    state_selections: selections,
                    sku_filter: effectiveSkuFilter || undefined,
                    target: chipTarget,
                    env: mqttEnv,
                };
                if (publishSchedule === 'scheduled' && publishScheduleAt) {
                    body.schedule_at = new Date(publishScheduleAt).toISOString();
                }
                const response = await apiService.publishAdvanced(publishModal.firmwareId, body);
                if (response.success) {
                    const d = response.data || {};
                    setPublishResult({
                        type: 'success',
                        message: `Advanced OTA queued. Job ${d.job_id}. Batch ${d.ota_batch_id}. Stations: ${d.total_stations ?? '…'}. Publishing in background…`,
                    });
                    if (d.job_id) {
                        const final = await pollOtaJob(d.job_id);
                        if (final) {
                            setPublishResult({
                                type: final.status === 'completed' ? 'success' : 'error',
                                message:
                                    final.message ||
                                    `Job ${final.status}: ${final.success_count || 0} ok, ${final.fail_count || 0} failed across ${final.processed_stations}/${final.total_stations} stations.`,
                            });
                        }
                    }
                    loadFirmware();
                } else {
                    setPublishResult({ type: 'error', message: response.error || 'Publish failed' });
                }
            } else {
                const ids = [...publishDeviceIdChips, ...publishDeviceIdInput.trim().split(/[\s,]+/).filter(Boolean)];
                const stationFromInput = publishStationIdInput.trim().split(/[\s,]+/).filter(Boolean);
                const stationId =
                    publishStationIdChips[0] ||
                    stationFromInput[stationFromInput.length - 1] ||
                    '0';
                const body = {
                    device_ids: ids,
                    station_id: stationId,
                    env: mqttEnv,
                };
                if (publishSchedule === 'scheduled' && publishScheduleAt) {
                    body.schedule_at = new Date(publishScheduleAt).toISOString();
                }
                const response = await apiService.publishFirmware(publishModal.firmwareId, body);
                if (response.success) {
                    const d = response.data || {};
                    setPublishResult({
                        type: 'success',
                        message: `Published to ${d.success_count ?? 0} device(s). Batch: ${d.ota_batch_id || '—'}. Target: ${d.target}, Version: ${d.version}`,
                    });
                    loadFirmware();
                } else {
                    setPublishResult({ type: 'error', message: response.error || 'Publish failed' });
                }
            }
        } catch (error) {
            setPublishResult({ type: 'error', message: error.response?.data?.error || error.message || 'Publish failed' });
        } finally {
            setPublishLoading(false);
        }
    };

    const topics = publishPreview?.topics || null;
    const advTopics = advancedPreview?.topics || { station_wide: [], device_short: [] };
    const advTopicCount =
        (advTopics.station_wide?.length || 0) + (advTopics.device_short?.length || 0);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Firmware Management</h1>
                {isAdmin() && (
                    <Link
                        to="/firmware/upload"
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                        Upload Firmware
                    </Link>
                )}
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Filters</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Device Type
                        </label>
                        <select
                            value={filters.device_id}
                            onChange={(e) => setFilters({...filters, device_id: e.target.value})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">All SKUs</option>
                            {devices.map(device => (
                                <option key={device.id} value={device.id}>
                                    {device.name}
                                    {device.platformio_env ? ` (${device.platformio_env})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Version
                        </label>
                        <input
                            type="text"
                            value={filters.version}
                            onChange={(e) => setFilters({...filters, version: e.target.value})}
                            placeholder="e.g., 1.0.0"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Limit
                        </label>
                        <select
                            value={filters.limit}
                            onChange={(e) => setFilters({...filters, limit: parseInt(e.target.value)})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value={10}>10 items</option>
                            <option value={25}>25 items</option>
                            <option value={50}>50 items</option>
                            <option value={100}>100 items</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white shadow rounded-lg">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="spinner mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading firmware...</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="p-6">
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-red-800">{error}</p>
                            <button 
                                onClick={loadFirmware}
                                className="mt-2 text-red-600 hover:text-red-800 underline"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                ) : firmware.length === 0 ? (
                    <div className="p-6 text-center">
                        <p className="text-gray-500">No firmware found</p>
                        {isAdmin() && (
                            <Link
                                to="/firmware/upload"
                                className="mt-2 inline-block text-primary-600 hover:text-primary-800 underline"
                            >
                                Upload your first firmware
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Device & Version
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        File Info
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Upload Info
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {firmware.map((fw) => {
                                    const deviceMeta = (devices || []).find(
                                        (d) => d.name === fw.device_name || d.id === fw.device_id
                                    );
                                    const applicable = deviceMeta?.applicable_skus || [];
                                    return (
                                    <tr key={fw.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {fw.device_name}
                                                    {deviceMeta?.platformio_env
                                                        ? ` · ${deviceMeta.platformio_env}`
                                                        : ''}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Version {fw.version}
                                                </div>
                                                {applicable.length > 0 && (
                                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                                        {applicable.map((s) => (
                                                            <span
                                                                key={s.sku_code}
                                                                className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                                                            >
                                                                {s.sku_code}
                                                                {s.power_rating_kw != null
                                                                    ? ` ${s.power_rating_kw} kW`
                                                                    : ''}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm text-gray-900">
                                                    {fw.file_name}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {apiService.formatFileSize(fw.file_size)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm text-gray-900">
                                                    {fw.uploaded_by}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {apiService.formatDate(fw.created_at)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDownload(fw.id, fw.file_name)}
                                                    className="p-1.5 rounded text-primary-600 hover:text-primary-900 hover:bg-primary-50"
                                                    title="Download"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                </button>
                                                {isAdmin() && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => openPublishModal(fw.id, fw.device_name, fw.version)}
                                                            className="p-1.5 rounded text-green-600 hover:text-green-900 hover:bg-green-50"
                                                            title="Publish"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(fw.id, fw.device_name, fw.version)}
                                                            className="p-1.5 rounded text-red-600 hover:text-red-900 hover:bg-red-50"
                                                            title="Delete"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal
                open={publishModal.open}
                onClose={closePublishModal}
                title={`Publish ${publishModal.deviceName} v${publishModal.version}`}
                maxWidth="max-w-4xl"
            >
                <div className="space-y-4">
                    {/* MQTT env — applies to Quick + Advanced publishes and topic previews */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-sm font-medium text-gray-700">MQTT environment</label>
                            <span className="text-xs text-gray-500">
                                Topics: <code className="bg-gray-100 px-1 rounded">{mqttEnv}/…/cmd/ota</code>
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2" role="group" aria-label="MQTT environment">
                            {MQTT_ENV_OPTIONS.map((opt) => {
                                const selected = mqttEnv === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setMqttEnvAndPersist(opt.value)}
                                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                                            selected
                                                ? opt.value === 'prod'
                                                    ? 'bg-emerald-600 border-emerald-600 text-white'
                                                    : opt.value === 'staging'
                                                      ? 'bg-amber-500 border-amber-500 text-white'
                                                      : 'bg-sky-600 border-sky-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                        aria-pressed={selected}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Applies to all publishes in this session. Saved in this browser (default Prod).
                        </p>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200">
                        <button
                            type="button"
                            onClick={() => setPublishTab('quick')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                                publishTab === 'quick'
                                    ? 'border-primary-600 text-primary-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Quick OTA
                        </button>
                        <button
                            type="button"
                            onClick={() => setPublishTab('advanced')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                                publishTab === 'advanced'
                                    ? 'border-primary-600 text-primary-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Advanced
                        </button>
                    </div>

                    {publishTab === 'quick' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Station ID</label>
                                <div className="flex flex-wrap gap-2 items-center border border-gray-300 rounded-md px-3 py-2 min-h-[2.5rem] bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
                                    {publishStationIdChips.map((id, index) => (
                                        <span
                                            key={`${id}-${index}`}
                                            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-slate-100 text-slate-800 text-sm"
                                        >
                                            {id}
                                            <button
                                                type="button"
                                                onClick={() => removeStationIdChip(index)}
                                                className="rounded-full p-0.5 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                aria-label={`Remove ${id}`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        value={publishStationIdInput}
                                        onChange={(e) => setPublishStationIdInput(e.target.value)}
                                        onKeyDown={handlePublishStationIdKeyDown}
                                        onBlur={addStationIdChipsFromInput}
                                        onPaste={handlePublishStationIdPaste}
                                        placeholder={
                                            publishStationIdChips.length === 0
                                                ? 'Type ID and press space or comma (default: 0)'
                                                : ''
                                        }
                                        className="flex-1 min-w-[120px] border-0 p-0 text-sm focus:outline-none focus:ring-0"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Install station id used in MQTT path. Leave empty for <code className="bg-gray-100 px-1 rounded">0</code>.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Device IDs</label>
                                <div className="flex flex-wrap gap-2 items-center border border-gray-300 rounded-md px-3 py-2 min-h-[2.5rem] bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
                                    {publishDeviceIdChips.map((id, index) => (
                                        <span
                                            key={`${id}-${index}`}
                                            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-primary-100 text-primary-800 text-sm"
                                        >
                                            {id}
                                            <button
                                                type="button"
                                                onClick={() => removeDeviceIdChip(index)}
                                                className="rounded-full p-0.5 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                aria-label={`Remove ${id}`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        value={publishDeviceIdInput}
                                        onChange={(e) => setPublishDeviceIdInput(e.target.value)}
                                        onKeyDown={handlePublishDeviceIdKeyDown}
                                        onBlur={addDeviceIdChipsFromInput}
                                        onPaste={handlePublishDeviceIdPaste}
                                        placeholder={publishDeviceIdChips.length === 0 ? 'Type ID and press space or comma' : ''}
                                        className="flex-1 min-w-[120px] border-0 p-0 text-sm focus:outline-none focus:ring-0"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Leave empty to use the firmware device. Quick OTA publishes the connector form.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">When to publish</label>
                                <div className="flex flex-wrap gap-4">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            name="schedule"
                                            checked={publishSchedule === 'immediate'}
                                            onChange={() => setPublishSchedule('immediate')}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm">Immediate</span>
                                    </label>
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            name="schedule"
                                            checked={publishSchedule === 'scheduled'}
                                            onChange={() => setPublishSchedule('scheduled')}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm">Schedule</span>
                                    </label>
                                </div>
                                {publishSchedule === 'scheduled' && (
                                    <input
                                        type="datetime-local"
                                        value={publishScheduleAt}
                                        onChange={(e) => setPublishScheduleAt(e.target.value)}
                                        className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Canonical topic forms</label>
                                <div className="bg-gray-50 rounded-md p-3 text-sm font-mono space-y-2">
                                    {topics ? (
                                        <>
                                            <div>
                                                <span className="text-gray-500 text-xs uppercase tracking-wide">Station-wide</span>
                                                <div className="text-primary-700 break-all">{topics.stationWide}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-xs uppercase tracking-wide">Device short</span>
                                                <div className="text-primary-700 break-all">{topics.deviceShort}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-xs uppercase tracking-wide">Connector (published)</span>
                                                <div className="text-green-700 break-all font-semibold">{topics.connectorForm}</div>
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-gray-500">Loading preview…</span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Payload preview</label>
                                <div className="bg-gray-50 rounded-md p-3 text-sm font-mono overflow-auto max-h-40">
                                    {publishPreview?.payload ? (
                                        <pre className="text-gray-800 whitespace-pre-wrap break-words">{JSON.stringify(
                                            publishSchedule === 'scheduled' && publishScheduleAt
                                                ? { ...publishPreview.payload, schedule: Math.floor(new Date(publishScheduleAt).getTime() / 1000) }
                                                : publishPreview.payload,
                                            null,
                                            2
                                        )}</pre>
                                    ) : (
                                        <span className="text-gray-500">Loading…</span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {publishTab === 'advanced' && (
                        <>
                            {/* State multi-select — collapsed shows chips only */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">States</label>
                                    <button
                                        type="button"
                                        onClick={() => setStatesPickerOpen((v) => !v)}
                                        className="text-xs text-primary-700 hover:text-primary-900"
                                    >
                                        {statesPickerOpen ? 'Collapse' : 'Choose states'}
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {selectedStates.length === 0 ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-800 text-xs font-medium">
                                            All India
                                        </span>
                                    ) : (
                                        selectedStates.map((s) => (
                                            <span
                                                key={s}
                                                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-primary-100 text-primary-800 text-xs font-medium"
                                            >
                                                {s}
                                                <button
                                                    type="button"
                                                    onClick={() => removeStateChip(s)}
                                                    className="rounded-full p-0.5 hover:bg-primary-200"
                                                    aria-label={`Remove ${s}`}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))
                                    )}
                                </div>
                                {statesPickerOpen && (
                                    <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-1 mb-2">
                                        {indiaStates.length === 0 && (
                                            <p className="text-xs text-gray-500 col-span-2">Loading states…</p>
                                        )}
                                        {indiaStates.map((s) => (
                                            <label
                                                key={s}
                                                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-sm cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStates.includes(s)}
                                                    onChange={() => toggleStateChip(s)}
                                                    className="text-primary-600 focus:ring-primary-500"
                                                />
                                                <span>{s}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500">
                                    No state selected = All India. Pick one or more states for state-wise station tables.
                                </p>
                            </div>

                            {/* State-wise paginated station tables */}
                            {!advancedInitialized ? (
                                <p className="text-sm text-gray-500">Loading stations…</p>
                            ) : (
                                <div className="space-y-4">
                                    {tableKeys.map((key) => {
                                        const t = stateTables[key] || emptyStateTable();
                                        const title = key === ALL_INDIA_KEY ? 'All India' : key;
                                        const totalPages = Math.max(1, Math.ceil((t.total || 0) / (t.pageSize || 100)));
                                        const selectedCount = t.selectAll
                                            ? Math.max(0, (t.total || 0) - (t.excludeIds || []).length)
                                            : (t.includeIds || []).length;
                                        return (
                                            <div key={key} className="border border-gray-200 rounded-md overflow-hidden">
                                                <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
                                                    <span className="text-sm font-semibold text-gray-900">{title}</span>
                                                    <span className="text-xs text-gray-500">
                                                        {t.total} station{t.total === 1 ? '' : 's'} · {selectedCount} selected
                                                    </span>
                                                    <label className="inline-flex items-center gap-1.5 text-sm ml-auto cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!t.selectAll}
                                                            onChange={() => toggleSelectAllStations(key)}
                                                            className="text-primary-600 focus:ring-primary-500"
                                                        />
                                                        All stations
                                                    </label>
                                                </div>
                                                <div className="px-3 py-2 border-b border-gray-100">
                                                    <input
                                                        type="search"
                                                        value={t.search || ''}
                                                        onChange={(e) => {
                                                            const q = e.target.value;
                                                            setStateTables((prev) => ({
                                                                ...prev,
                                                                [key]: { ...(prev[key] || emptyStateTable()), search: q },
                                                            }));
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                loadStateTablePage(key, 1, t.search || '');
                                                            }
                                                        }}
                                                        placeholder="Search this table… (Enter)"
                                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    />
                                                </div>
                                                {t.loading ? (
                                                    <p className="px-3 py-4 text-sm text-gray-500">Loading…</p>
                                                ) : (t.stations || []).length === 0 ? (
                                                    <p className="px-3 py-4 text-sm text-amber-700">No stations in this scope.</p>
                                                ) : (
                                                    <table className="min-w-full text-sm">
                                                        <thead className="bg-white text-left text-xs text-gray-500 uppercase">
                                                            <tr>
                                                                <th className="px-3 py-2 w-10" />
                                                                <th className="px-3 py-2">Name</th>
                                                                <th className="px-3 py-2">ID</th>
                                                                <th className="px-3 py-2">City</th>
                                                                <th className="px-3 py-2 text-right">Devices</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {t.stations.map((st) => (
                                                                <tr key={st.id} className="hover:bg-gray-50">
                                                                    <td className="px-3 py-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isStationChecked(key, st.id)}
                                                                            onChange={() => toggleStationInTable(key, st.id)}
                                                                            className="text-primary-600 focus:ring-primary-500"
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2 font-medium text-gray-900">{st.name}</td>
                                                                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{st.id}</td>
                                                                    <td className="px-3 py-2 text-gray-600">{st.city || '—'}</td>
                                                                    <td className="px-3 py-2 text-right text-gray-500">
                                                                        {st.device_count != null ? st.device_count : '—'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                                <div className="flex items-center justify-between px-3 py-2 bg-white border-t border-gray-100 text-xs text-gray-600">
                                                    <span>
                                                        Page {t.page || 1} of {totalPages}
                                                        {t.selectAll ? ' · All stations selected across pages' : ''}
                                                    </span>
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            disabled={(t.page || 1) <= 1 || t.loading}
                                                            onClick={() => loadStateTablePage(key, (t.page || 1) - 1, t.search || '')}
                                                            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                                                        >
                                                            Prev
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={(t.page || 1) >= totalPages || t.loading}
                                                            onClick={() => loadStateTablePage(key, (t.page || 1) + 1, t.search || '')}
                                                            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Device visualiser — capped sample; section collapsed by default */}
                            {advancedPreview?.stations?.length > 0 && (
                                <div className="border border-gray-200 rounded-md">
                                    <button
                                        type="button"
                                        onClick={() => setDevicesSectionOpen((v) => !v)}
                                        className="w-full flex justify-between items-center px-3 py-2 text-sm text-left hover:bg-gray-50"
                                    >
                                        <span className="font-medium text-gray-700">
                                            Devices in selection (sample)
                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                SKU{' '}
                                                {advancedPreview.summary?.sku_filter ||
                                                    advancedPreview.target ||
                                                    effectiveSkuFilter ||
                                                    '—'}
                                                {' · '}
                                                {advancedPreview.summary?.stations_count ?? advancedPreview.stations.length} station
                                                {(advancedPreview.summary?.stations_count ?? advancedPreview.stations.length) === 1 ? '' : 's'}
                                                {' · '}
                                                station-wide OTA
                                            </span>
                                        </span>
                                        <span className="text-gray-400">{devicesSectionOpen ? '▾' : '▸'}</span>
                                    </button>
                                    {devicesSectionOpen && (
                                        <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                                            <p className="text-xs text-gray-500 pt-2">
                                                Only devices matching the designated SKU are listed. Stations with 0 matches appear last.
                                                Showing up to {PREVIEW_STATIONS_UI_LIMIT} stations
                                                {advancedPreview.summary?.stations_count > PREVIEW_STATIONS_UI_LIMIT
                                                    ? ` of ${advancedPreview.summary.stations_count}`
                                                    : ''}
                                                , {PREVIEW_DEVICES_PER_STATION_UI} devices each.
                                            </p>
                                            {[...advancedPreview.stations]
                                                .sort((a, b) => {
                                                    const ac = a.device_count != null ? Number(a.device_count) : (a.devices || []).length;
                                                    const bc = b.device_count != null ? Number(b.device_count) : (b.devices || []).length;
                                                    if (ac === 0 && bc > 0) return 1;
                                                    if (bc === 0 && ac > 0) return -1;
                                                    return 0;
                                                })
                                                .slice(0, PREVIEW_STATIONS_UI_LIMIT)
                                                .map((st) => {
                                                const allDevices = st.devices || [];
                                                const shownDevices = allDevices.slice(0, PREVIEW_DEVICES_PER_STATION_UI);
                                                const deviceTotal =
                                                    st.device_count != null
                                                        ? Number(st.device_count)
                                                        : allDevices.length;
                                                return (
                                                    <div key={st.id} className="border border-gray-200 rounded-md">
                                                        <button
                                                            type="button"
                                                            className="w-full flex justify-between items-center px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-t-md"
                                                            onClick={() =>
                                                                setExpandedStationCards((prev) => ({
                                                                    ...prev,
                                                                    [st.id]: !prev[st.id],
                                                                }))
                                                            }
                                                        >
                                                            <span className="font-medium truncate">{st.id}</span>
                                                            <span className="text-xs text-gray-500 shrink-0 ml-2">
                                                                {deviceTotal} device{deviceTotal === 1 ? '' : 's'} ·{' '}
                                                                {expandedStationCards[st.id] ? '▾' : '▸'}
                                                            </span>
                                                        </button>
                                                        {expandedStationCards[st.id] && (
                                                            <ul className="px-3 py-2 space-y-1 text-sm max-h-36 overflow-y-auto">
                                                                {shownDevices.length === 0 && (
                                                                    <li className="text-gray-400 text-xs">
                                                                        No devices in sample. Station-wide topic will still be published.
                                                                    </li>
                                                                )}
                                                                {shownDevices.map((d) => (
                                                                    <li
                                                                        key={d.id}
                                                                        className={`flex items-center gap-2 ${
                                                                            d.role === 'tethered' ? 'text-gray-400' : 'text-gray-800'
                                                                        }`}
                                                                    >
                                                                        <span className="font-mono text-xs truncate">{d.id}</span>
                                                                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 shrink-0">
                                                                            {d.device_type || '—'}
                                                                        </span>
                                                                        <span className="text-xs ml-auto shrink-0">
                                                                            {d.role === 'gateway'
                                                                                ? 'Gateway'
                                                                                : d.role === 'tethered'
                                                                                  ? 'via gateway'
                                                                                  : 'Standalone'}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                                {deviceTotal > shownDevices.length && (
                                                                    <li className="text-xs text-gray-400 pt-1">
                                                                        …and {deviceTotal - shownDevices.length} more not shown
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {(advancedPreview.summary?.stations_count ||
                                                advancedPreview.stations.length) > PREVIEW_STATIONS_UI_LIMIT && (
                                                <p className="text-xs text-gray-500">
                                                    …and{' '}
                                                    {(advancedPreview.summary?.stations_count ||
                                                        advancedPreview.stations.length) - PREVIEW_STATIONS_UI_LIMIT}{' '}
                                                    more stations not listed
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU / target filter</label>
                                    <select
                                        value={skuFilter}
                                        onChange={(e) => setSkuFilter(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        {skuOptions.map((o) => (
                                            <option key={o.value || 'fw'} value={o.value}>
                                                {o.value === '' && publishModal.deviceName
                                                    ? `Firmware SKU (${publishModal.deviceName})`
                                                    : o.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Device sample &amp; impact counts only include this SKU.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Chip target</label>
                                    <select
                                        value={chipTarget}
                                        onChange={(e) => setChipTarget(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="esp32">esp32</option>
                                        <option value="n58">n58</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">When to publish</label>
                                <div className="flex flex-wrap gap-4">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            name="adv-schedule"
                                            checked={publishSchedule === 'immediate'}
                                            onChange={() => setPublishSchedule('immediate')}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm">Immediate</span>
                                    </label>
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            name="adv-schedule"
                                            checked={publishSchedule === 'scheduled'}
                                            onChange={() => setPublishSchedule('scheduled')}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm">Schedule</span>
                                    </label>
                                </div>
                                {publishSchedule === 'scheduled' && (
                                    <input
                                        type="datetime-local"
                                        value={publishScheduleAt}
                                        onChange={(e) => setPublishScheduleAt(e.target.value)}
                                        className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                )}
                            </div>

                            <div className="rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm">
                                <div className="font-medium text-primary-900 mb-1">Impact summary</div>
                                {advancedPreview?.summary ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-primary-800">
                                        <div>
                                            <div className="text-xs text-primary-600">Stations</div>
                                            <div className="text-lg font-semibold">{advancedPreview.summary.stations_count}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-primary-600">Gateways</div>
                                            <div className="text-lg font-semibold">{advancedPreview.summary.gateway_devices}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-primary-600">Standalone</div>
                                            <div className="text-lg font-semibold">{advancedPreview.summary.standalone_devices}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-primary-600">Tethered (via hub)</div>
                                            <div className="text-lg font-semibold">{advancedPreview.summary.tethered_children}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-primary-700 text-xs">Select stations to see impact.</p>
                                )}
                                {advancedPreview?.ota_batch_id_preview && (
                                    <p className="mt-2 text-xs text-primary-700 font-mono break-all">
                                        ota_batch_id preview: {advancedPreview.ota_batch_id_preview}
                                    </p>
                                )}
                            </div>

                            <div className="border border-gray-200 rounded-md">
                                <button
                                    type="button"
                                    onClick={() => setTopicsExpanded((v) => !v)}
                                    className="w-full flex justify-between items-center px-3 py-2 text-sm text-left hover:bg-gray-50"
                                >
                                    <span className="font-medium text-gray-700">
                                        Topics to be published ({advTopicCount}
                                        {advancedPreview?.summary?.preview_capped ? ' sample' : ''})
                                    </span>
                                    <span className="text-gray-400">{topicsExpanded ? '▾' : '▸'}</span>
                                </button>
                                {topicsExpanded && (
                                    <div className="px-3 pb-3 max-h-48 overflow-y-auto text-xs font-mono space-y-3">
                                        <div>
                                            <div className="text-gray-500 uppercase tracking-wide mb-1">Station-wide</div>
                                            {(advTopics.station_wide || []).length === 0 && (
                                                <div className="text-gray-400">—</div>
                                            )}
                                            {(advTopics.station_wide || []).map((t) => (
                                                <div key={t} className="text-primary-700 break-all py-0.5">{t}</div>
                                            ))}
                                        </div>
                                        <div>
                                            <div className="text-gray-500 uppercase tracking-wide mb-1">Device short</div>
                                            {(advTopics.device_short || []).length === 0 && (
                                                <div className="text-gray-400">— (station-wide only if no gateways resolved)</div>
                                            )}
                                            {(advTopics.device_short || []).map((t) => (
                                                <div key={t} className="text-green-700 break-all py-0.5">{t}</div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {otaJobProgress && (
                        <div className="rounded-lg p-3 text-sm bg-blue-50 text-blue-900 border border-blue-200">
                            Job {otaJobProgress.status}: {otaJobProgress.processed_stations}/{otaJobProgress.total_stations} stations
                            · {otaJobProgress.success_count || 0} ok / {otaJobProgress.fail_count || 0} failed
                            {otaJobProgress.message ? ` — ${otaJobProgress.message}` : ''}
                        </div>
                    )}

                    {publishResult && (
                        <div className={`rounded-lg p-3 text-sm ${publishResult.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                            {publishResult.message}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={closePublishModal}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handlePublishSubmit}
                            disabled={
                                publishLoading ||
                                (publishTab === 'advanced' && !hasStationSelection)
                            }
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                        >
                            {publishLoading
                                ? publishTab === 'advanced'
                                    ? 'Queuing / publishing…'
                                    : 'Publishing…'
                                : publishTab === 'advanced'
                                  ? 'Publish Advanced OTA'
                                  : 'Publish'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default FirmwareList;
